/**
 * useAuth — thin hook wrapping the auth service layer.
 *
 * Provides session state and loading flag.
 * Components MUST NOT import from src/lib/supabase.ts — they use this hook.
 */

import { useEffect, useRef, useState } from "react";
import { getSession, onAuthStateChange } from "../lib/api/auth";
import { migrateGuestProjectsToUser } from "../lib/api/projects";
import type { AuthSession } from "../types/auth";

interface UseAuthResult {
  session: AuthSession | null;
  loading: boolean;
}

export function useAuth(): UseAuthResult {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [loading, setLoading] = useState(true);

  // Track whether we already migrated this session's guest projects
  // so a spurious auth re-emit doesn't double-insert rows.
  const migratedUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Hydrate session from storage on mount
    getSession().then((s) => {
      setSession(s);
      setLoading(false);
      if (s && migratedUserIdRef.current !== s.user.id) {
        migratedUserIdRef.current = s.user.id;
        void migrateGuestProjectsToUser();
      }
    });

    // Subscribe to future auth state changes
    const unsub = onAuthStateChange((s) => {
      setSession(s);
      setLoading(false);
      if (s && migratedUserIdRef.current !== s.user.id) {
        // A-07 — fire-and-forget migration on sign-in. Service layer
        // is idempotent (drains localStorage on success).
        migratedUserIdRef.current = s.user.id;
        void migrateGuestProjectsToUser();
      } else if (!s) {
        migratedUserIdRef.current = null;
      }
    });

    return unsub;
  }, []);

  return { session, loading };
}
